import { getDocument } from '@atlaskit/browser-apis';

import Image from '@atlaskit/image';

import { Text } from '@atlaskit/primitives/compiled';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
// @ts-expect-error: 'React' is declared but its value is never read
// React import required to render in Jira SSR
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { spotlightMessages } from './messages';

import ListMergeSpotlightIllustrationDark from '../assets/list-merge-spotlight-dark.gif';

import ListMergeSpotlightIllustration from '../assets/list-merge-spotlight-light.gif';

import { type JSX } from 'react/jsx-runtime';

import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

export const TELL_ME_MORE_URL =
	'https://community.atlassian.com/forums/Jira-articles/We-re-merging-List-and-All-work-one-powerful-view-for-all-teams/ba-p/3090105';

const SPOTLIGHT_TARGET_NEW = '#horizontal-nav-tabs-LIST';

type ListMergeSpotlightTourProps = {
	/**
		Expose prop to hide the illustration for VR testing
		**/
	withIllustration?: boolean;
};

const useListMergeHandlers = () => {
	const { onClosed } = usePostOfficeUserInteractions();
	const [isActive, setIsActive] = useState(true);

	const handleDismiss = useCallback(() => {
		setIsActive(false);
		void onClosed({ interactionIdentifier: 'dismiss' });
	}, [onClosed]);

	const handleTellMeMore = useCallback(() => {
		// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-all-member-expressions
		window.open(TELL_ME_MORE_URL);
		handleDismiss();
	}, [handleDismiss]);

	useEffect(() => {
		const handleEscape = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				handleDismiss();
			}
		};

		getDocument()?.addEventListener('keydown', handleEscape);

		return () => {
			getDocument()?.removeEventListener('keydown', handleEscape);
		};
	}, [handleDismiss]);

	return { isActive, handleDismiss, handleTellMeMore };
};

export const ListMergeSpotlightTour = ({
	withIllustration = true,
}: ListMergeSpotlightTourProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { isActive, handleDismiss, handleTellMeMore } = useListMergeHandlers();
	const renderInline = useMountToHtmlElement({
		selectors: SPOTLIGHT_TARGET_NEW,
		as: 'portal',
		position: 'inside',
	});

	return (
		<div>
			{renderInline(
				<PopoverProvider>
					<PopoverTarget>
						<div />
					</PopoverTarget>
					<PopoverContent dismiss={handleDismiss} placement="bottom-end" isVisible={isActive}>
						<SpotlightCard>
							<SpotlightHeader>
								<SpotlightHeadline>
									{formatMessage(spotlightMessages.spotlightHeading)}
								</SpotlightHeadline>
							</SpotlightHeader>
							{withIllustration && (
								<SpotlightMedia>
									<Image
										src={ListMergeSpotlightIllustration}
										srcDark={ListMergeSpotlightIllustrationDark}
										alt=""
									/>
								</SpotlightMedia>
							)}
							<SpotlightBody>
								<Text>{formatMessage(spotlightMessages.spotlightBody)}</Text>
							</SpotlightBody>
							<SpotlightFooter>
								<SpotlightActions>
									<SpotlightSecondaryAction onClick={handleDismiss}>
										{formatMessage(spotlightMessages.spotlightDismiss)}
									</SpotlightSecondaryAction>
									<SpotlightPrimaryAction
										onClick={handleTellMeMore}
										aria-label={formatMessage(spotlightMessages.tellMeMoreAriaLabel)}
									>
										{formatMessage(spotlightMessages.spotlightTellMeMore)}
									</SpotlightPrimaryAction>
								</SpotlightActions>
							</SpotlightFooter>
						</SpotlightCard>
					</PopoverContent>
				</PopoverProvider>,
			)}
		</div>
	);
};
